<template>
  <div class="table-container">
    <div class="fixed-header">
      <el-button type="primary" :disabled="loading" @click="openDialog(null)">新建游戏配置</el-button>
      <p class="hint">游戏端拉取配置：<code>GET /api/v1/passthrough/game-config?channel=渠道参数值</code></p>
    </div>

    <div class="table-wrapper">
      <el-table
        v-loading="loading"
        class="passthrough-game-table"
        :data="data"
        height="calc(100vh - 240px)"
        style="width: 100%"
        table-layout="fixed"
      >
        <el-table-column label="ID" width="100" prop="id" show-overflow-tooltip />
        <el-table-column label="游戏名称" min-width="160" prop="gameName" show-overflow-tooltip />
        <el-table-column label="类型" width="88" align="center">
          <template #default="scope">
            {{ scope.row.gameType === 'portrait' ? '竖屏' : '横屏' }}
          </template>
        </el-table-column>
        <el-table-column label="透传" width="80" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.passthroughEnabled ? 'success' : 'info'">
              {{ scope.row.passthroughEnabled ? '开' : '关' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="渠道参数" min-width="140" prop="channelParam" show-overflow-tooltip />
        <el-table-column label="透传 URL" min-width="120">
          <template #default="scope">
            <el-tooltip
              v-if="(scope.row.passthroughUrls || []).length"
              :content="(scope.row.passthroughUrls || []).join('\n')"
              placement="top"
            >
              <span>{{ (scope.row.passthroughUrls || []).length }} 条</span>
            </el-tooltip>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right" align="center">
          <template #default="scope">
            <el-button size="small" :disabled="loading" @click="openPreview(scope.row)">预览</el-button>
            <el-button size="small" :disabled="loading" @click="openDialog(scope.row)">编辑</el-button>
            <el-button size="small" type="danger" :disabled="loading" @click="remove(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="fixed-pagination">
      <div style="display: flex; justify-content: space-between">
        <el-pagination
          layout="prev, pager, next"
          :total="total"
          :page-size="query.limit || 50"
          @current-change="pageChange"
          :disabled="loading"
        />
        <el-select v-model="query.limit" style="width: 100px" @change="reload" :disabled="loading">
          <el-option :value="20" />
          <el-option :value="50" />
          <el-option :value="100" />
        </el-select>
      </div>
    </div>

    <el-dialog v-model="previewDialogOpened" :title="previewTitle" width="560px" destroy-on-close>
      <p v-if="!previewUrls.length" class="field-tip">暂无透传 URL</p>
      <ul v-else class="preview-url-list">
        <li v-for="(u, idx) in previewUrls" :key="idx">
          <el-link type="primary" :href="u" target="_blank" rel="noopener noreferrer">{{ u }}</el-link>
        </li>
      </ul>
    </el-dialog>

    <el-dialog v-model="dialogOpened" :title="editing ? '编辑游戏透传' : '新建游戏透传'" width="720px" :before-close="reset">
      <el-form :model="form" label-width="140px">
        <el-form-item label="游戏名称" required>
          <el-input v-model="form.gameName" placeholder="名称" />
        </el-form-item>
        <el-form-item label="游戏类型" required>
          <el-select v-model="form.gameType" style="width: 100%">
            <el-option label="横屏" value="landscape" />
            <el-option label="竖屏" value="portrait" />
          </el-select>
        </el-form-item>
        <el-form-item label="开启透传">
          <el-switch v-model="form.passthroughEnabled" />
        </el-form-item>
        <el-form-item label="渠道参数值" required>
          <el-input v-model="form.channelParam" placeholder="与游戏端请求参数 channel 一致，全局唯一" />
        </el-form-item>
        <el-form-item label="透传 URL">
          <el-select
            v-model="form.passthroughUrls"
            multiple
            filterable
            allow-create
            default-first-option
            :reserve-keyword="false"
            placeholder="输入完整 URL 后回车添加，可多条"
            style="width: 100%"
          />
          <div class="field-tip">可选，最多 50 条；空串会自动忽略；重复项保存时会去重</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button type="primary" :loading="loading" text @click="reset">取消</el-button>
        <el-button type="primary" :loading="loading" @click="submit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { PassthroughGameApi, PassthroughGameRow } from '../../api/passthroughApi';

export default defineComponent({
  name: 'PassthroughGame',
  data() {
    return {
      loading: false,
      data: [] as PassthroughGameRow[],
      editing: null as PassthroughGameRow | null,
      dialogOpened: false,
      previewDialogOpened: false,
      previewTitle: '',
      previewUrls: [] as string[],
      total: 0,
      query: { limit: 50, skip: 0 },
      form: {
        gameName: '',
        gameType: 'landscape' as 'landscape' | 'portrait',
        passthroughEnabled: false,
        channelParam: '',
        passthroughUrls: [] as string[],
      },
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        const result = await PassthroughGameApi.list(this.query.skip, this.query.limit);
        this.data = result.list;
        this.total = result.total;
      } catch (e) {
        ElMessage.error('加载失败');
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    pageChange(page: number) {
      this.query.skip = (page - 1) * this.query.limit;
      this.init();
    },
    reload() {
      this.query.skip = 0;
      this.init();
    },
    openDialog(row: PassthroughGameRow | null) {
      this.editing = row;
      if (row) {
        this.form = {
          gameName: row.gameName,
          gameType: row.gameType,
          passthroughEnabled: row.passthroughEnabled,
          channelParam: row.channelParam,
          passthroughUrls: Array.isArray(row.passthroughUrls) ? [...row.passthroughUrls] : [],
        };
      } else {
        this.form = {
          gameName: '',
          gameType: 'landscape',
          passthroughEnabled: false,
          channelParam: '',
          passthroughUrls: [],
        };
      }
      this.dialogOpened = true;
    },
    reset() {
      this.dialogOpened = false;
      this.editing = null;
    },
    openPreview(row: PassthroughGameRow) {
      const urls = Array.isArray(row.passthroughUrls) ? row.passthroughUrls.filter((x) => x && String(x).trim()) : [];
      if (!urls.length) {
        ElMessage.warning('暂无透传 URL，请先编辑并添加地址');
        return;
      }
      this.previewTitle = `预览 — ${row.gameName}`;
      this.previewUrls = urls.map((u) => String(u).trim());
      this.previewDialogOpened = true;
    },
    async submit() {
      if (!this.form.gameName.trim() || !this.form.channelParam.trim()) {
        ElMessage.warning('请填写游戏名称与渠道参数值');
        return;
      }
      const body: Record<string, unknown> = { ...this.form };
      body.passthroughUrls = [...(this.form.passthroughUrls || [])];
      this.loading = true;
      try {
        if (this.editing) {
          await PassthroughGameApi.update(this.editing.id, body as any);
          ElMessage.success('已保存');
        } else {
          await PassthroughGameApi.create(body as any);
          ElMessage.success('已创建');
        }
        this.reset();
        await this.init();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '操作失败');
      } finally {
        this.loading = false;
      }
    },
    async remove(row: PassthroughGameRow) {
      try {
        await ElMessageBox.confirm(`确定删除「${row.gameName}」？`, '确认', { type: 'warning' });
      } catch {
        return;
      }
      this.loading = true;
      try {
        await PassthroughGameApi.remove(row.id);
        ElMessage.success('已删除');
        await this.init();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '删除失败');
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style scoped>
.table-container {
  padding: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.fixed-header {
  margin-bottom: 12px;
}
.hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.field-tip {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-top: 6px;
}
.table-wrapper {
  width: 100%;
  margin-bottom: 12px;
}

.passthrough-game-table {
  width: 100% !important;
}

.preview-url-list {
  margin: 0;
  padding-left: 1.25rem;
  word-break: break-all;
}

.preview-url-list li {
  margin-bottom: 10px;
}
.fixed-pagination {
  padding: 8px 0;
}
</style>
